import { ComplaintSummaryType, QuestionAnswer } from '@chiroup/core/types/ComplaintSummary.type';
import React from 'react';
import InteractiveValue from './InteractiveValue';

interface ProviderDisplayProps {
  questionData: QuestionAnswer;
  questionId: string;
  isEditable: boolean;
  popoverOpen: boolean;
  onPopoverOpenChange: (open: boolean) => void;
  onAnswerChange: (questionId: string, newValue: string | string[], otherValue?: string) => void;
  summaryType?: ComplaintSummaryType;
}

const ProviderDisplay: React.FC<ProviderDisplayProps> = ({
  questionData,
  questionId,
  isEditable,
  popoverOpen,
  onPopoverOpenChange,
  onAnswerChange,
  summaryType,
}) => {
  const { selectedValues = [], customInputData = {}, otherValue } = questionData;

  // Handle both customInputData and data field formats
  const inputData = (questionData as any).data || customInputData || {};

  // Filter out 'other' from selected values for display
  const providerSelections = selectedValues.filter(v => v !== 'other');

  // Get the custom input data for selected providers
  const providerData = providerSelections.map(provider => {
    const customInput = inputData[provider];

    return {
      provider,
      customInput: customInput && customInput.trim() ? customInput : undefined,
    };
  });

  // Add 'other' if selected
  if (selectedValues.includes('other') && otherValue) {
    providerData.push({
      provider: 'Other',
      customInput: otherValue,
    });
  }

  // Generate display text
  const generateDisplayText = () => {
    if (providerData.length === 0) {
      // For Initial/CCS summaries, show "None"
      // For Interim/Reeval summaries, show "denies"
      if (summaryType === ComplaintSummaryType.Initial) {
        return 'None';
      }

      return 'denies';
    }

    return providerData
      .map(({ provider, customInput }) => {
        if (customInput) {
          return `${provider}: "${customInput}"`;
        }

        return provider;
      })
      .join(', ');
  };

  return (
    <InteractiveValue
      questionId={questionId}
      questionData={questionData}
      displayValue={generateDisplayText()}
      isEditable={isEditable}
      popoverOpen={popoverOpen}
      onPopoverOpenChange={onPopoverOpenChange}
      onAnswerChange={onAnswerChange}
    />
  );
};

export default ProviderDisplay;
